// frontend/src/pages/admin/ESIMOrderManager.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { QRCodeSVG as QRCode } from "qrcode.react";
import { adminAPI } from "../../services/api";

const ESIMOrderManager = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllESIMOrders();
      setOrders(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateESIM = async (orderId) => {
    try {
      await adminAPI.activateESIM(orderId);
      fetchOrders();
    } catch (err) {
      setError(err.message);
    }
  };

  const columns = [
    {
      field: "createdAt",
      headerName: "Date",
      width: 180,
      valueFormatter: (params) => new Date(params.value).toLocaleString(),
    },
    { field: "packageType", headerName: "Package", width: 150 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Typography
          color={
            params.value === "active"
              ? "success.main"
              : params.value === "pending"
              ? "warning.main"
              : "error.main"
          }
        >
          {params.value.toUpperCase()}
        </Typography>
      ),
    },
    { field: "userId", headerName: "User ID", width: 220 },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <Box>
          <Button
            size="small"
            onClick={() => setSelectedOrder(params.row)}
            sx={{ mr: 1 }}
          >
            View Details
          </Button>
          {params.row.status === "pending" && (
            <Button
              size="small"
              variant="contained"
              onClick={() => handleActivateESIM(params.row.id)}
            >
              Activate
            </Button>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        eSIM Order Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ height: 600 }}>
        <DataGrid
          rows={orders}
          columns={columns}
          pageSize={10}
          loading={loading}
          disableSelectionOnClick
        />
      </Paper>

      <Dialog
        open={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>eSIM Order Details</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Order ID</Typography>
                <Typography>{selectedOrder.id}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">QR Code</Typography>
                {selectedOrder.esimData?.qrCode && (
                  <Box display="flex" justifyContent="center" my={2}>
                    <QRCode value={selectedOrder.esimData.qrCode} size={200} />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Activation Code</Typography>
                <Typography>
                  {selectedOrder.esimData?.activationCode}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Package Details</Typography>
                <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                  {JSON.stringify(
                    selectedOrder.esimData?.packageDetails,
                    null,
                    2
                  )}
                </pre>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedOrder(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ESIMOrderManager;
